import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";
import { SmartImg } from "./SmartImg";
import copy from "@/content/homepage-copy.json";

const resolve = (p) => (p.startsWith("http") || p.startsWith("/") ? p : `/${p}`);

const Frame = ({ label, src, alt, variant }) => (
  <figure className="flex-1">
    <figcaption className="mb-2 font-display text-sm font-medium text-brand-sec">{label}</figcaption>
    <div
      className={`relative aspect-[4/3] w-full overflow-hidden rounded-card border ${
        variant === "after" ? "border-brand-accent/40" : "border-brand-line"
      } bg-brand-surface`}
    >
      <SmartImg src={resolve(src)} alt={alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
      <span
        className={`absolute left-3 top-3 rounded-full border px-2.5 py-0.5 font-display text-[11px] font-medium ${
          variant === "after"
            ? "border-brand-accent/50 bg-brand-bg/70 text-brand-accent"
            : "border-brand-line bg-brand-bg/70 text-brand-muted"
        }`}
      >
        {label}
      </span>
    </div>
  </figure>
);

export const EffectsSection = () => {
  const e = copy.effects;
  const slides = e.slides || [];
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const containerRef = useRef(null);

  const go = (i) => setIndex((i + slides.length) % slides.length);

  useEffect(() => {
    if (reduce || paused || !e.autoplay || slides.length < 2) return;
    const id = setTimeout(() => setIndex((p) => (p + 1) % slides.length), e.intervalMs || 6000);
    return () => clearTimeout(id);
  }, [index, paused, reduce, e.autoplay, e.intervalMs, slides.length]);

  if (slides.length === 0) {
    return (
      <section id="efekty" className="mx-auto max-w-content px-4 py-14 md:px-8 md:py-[88px]" data-testid="effects-section">
        <SectionHeading chapter="02 — EFEKTY" title={e.title} />
      </section>
    );
  }

  const slide = slides[index];

  return (
    <section id="efekty" className="mx-auto max-w-content px-4 py-14 md:px-8 md:py-[88px]" data-testid="effects-section">
      <SectionHeading chapter="02 — EFEKTY" title={e.title} />

      <Reveal delay={0.1}>
        <div
          ref={containerRef}
          className="mt-14 rounded-panel border border-brand-line bg-brand-bg p-5 sm:p-8"
          role="group"
          aria-roledescription="karuzela"
          aria-label="Przykłady przed i po"
          data-testid="effects-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={(ev) => {
            if (!containerRef.current?.contains(ev.relatedTarget)) setPaused(false);
          }}
        >
          {/* category name above pair */}
          <div className="mb-4 flex items-center justify-between gap-4">
            <p className="font-display text-base font-semibold text-brand-text" data-testid="effects-category" aria-live="polite">
              {slide.category}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Poprzedni przykład"
                data-testid="effects-prev"
                onClick={() => go(index - 1)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-line text-brand-text transition-colors hover:border-brand-accent hover:text-brand-accent"
              >
                <ChevronLeft size={18} strokeWidth={1.75} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="Następny przykład"
                data-testid="effects-next"
                onClick={() => go(index + 1)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-line text-brand-text transition-colors hover:border-brand-accent hover:text-brand-accent"
              >
                <ChevronRight size={18} strokeWidth={1.75} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* pair (fade opacity, fixed layout height) */}
          <motion.div
            key={slide.id}
            initial={{ opacity: reduce ? 1 : 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-stretch gap-4 sm:flex-row"
          >
            <Frame label={e.beforeLabel} src={slide.beforeImageSrc} alt={slide.alt} variant="before" />
            <Frame label={e.afterLabel} src={slide.afterImageSrc} alt={slide.alt} variant="after" />
          </motion.div>

          {/* disclosure + demo/tool info */}
          <p className="mt-4 text-sm text-brand-sec" data-testid="effects-disclosure">
            {slide.disclosure}
          </p>
          <p className="mt-1 text-xs text-brand-muted">
            Materiały demonstracyjne (status: {e.status}). Narzędzie: {e.tool} · Test: {e.testedAt}.
          </p>

          {/* indicators */}
          <div className="mt-5 flex items-center justify-center gap-2" role="tablist" aria-label="Wybierz przykład">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-current={i === index}
                aria-label={`Pokaż przykład: ${s.category}`}
                data-testid={`effects-dot-${i}`}
                onClick={() => go(i)}
                className={`h-2.5 rounded-full transition-all duration-200 ${
                  i === index ? "w-7 bg-brand-accent" : "w-2.5 bg-brand-line hover:bg-brand-muted"
                }`}
              />
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
};
